
import { component } from '@/helpers/vueTyped';
import { Summary } from '@/api/validators/content';
import { FeedItem } from '@/api/validators/feed';
import { SummaryType } from '@/components/Sport/types';
import { dispatch } from '@/store';
import Rail from './Rail.vue';
import SummaryFetcher from '@/components/SharedViews/SummaryFetcher.vue';
import IntersectionObserverMarker from '@/components/Helpers/IntersectionObserverMarker.vue';
import InlineLoader from '@/components/SharedViews/InlineLoader.vue';

interface CardRailProps {
  feed: FeedItem[];
  summaryType: SummaryType;
  categoryName: string;
  cardFocusCallback: (summary: Summary) => void;
  firstCardLoadedCallback: (summary: Summary) => void;
  autoUpdate?: boolean;
  cacheSummary?: boolean;
  endReachedCallback?: () => void;
}

export default component<CardRailProps>()({
  name: 'CardRail',

  props: {
    feed: {},
    summaryType: {},
    categoryName: {},
    cardFocusCallback: {},
    firstCardLoadedCallback: {},
    autoUpdate: Boolean,
    cacheSummary: {
      type: Boolean,
      default: true,
    },
    endReachedCallback: {},
  },

  data() {
    return {
      lastFocusedCard: undefined as HTMLElement | undefined,
    };
  },

  methods: {
    cardFocusedCallback(event: FocusEvent, summary: Summary): void {
      this.cardFocusCallback(summary);
      this.setLastFocusedCard(event.target as HTMLElement);
    },

    setLastFocusedCard(element: HTMLElement): void {
      if (this.lastFocusedCard) {
        this.lastFocusedCard.classList.remove('last-focused');
      }

      this.lastFocusedCard = element;
      this.lastFocusedCard?.classList?.add('last-focused');
    },

    focus() {
      this.checkRailIsScrolled();

      if (this.lastFocusedCard) {
        this.lastFocusedCard.focus();
      } else {
        (this.$refs['card-0'] as InstanceType<typeof SummaryFetcher>).focus();
      }
    },

    firstCardRendered() {
      if (!this.lastFocusedCard) {
        this.$nextTick(() => {
          const card = (this.$refs['card-0'] as Vue).$refs.card as Vue;
          this.setLastFocusedCard(card?.$el as HTMLElement);
        });
      }
    },

    railStartObserved(entries: IntersectionObserverEntry[]) {
      dispatch('setDrawerHidden', [!entries[0].isIntersecting]);
    },

    railEndObserved(entries: IntersectionObserverEntry[]) {
      if (entries[0].isIntersecting) {
        this.endReachedCallback!();
      }
    },

    checkRailIsScrolled() {
      const rail = this.$refs.rail as HTMLElement | undefined;
      if (rail) {
        dispatch('setDrawerHidden', [rail.scrollLeft > 0]);
      }
    },
  },

  destroyed() {
    dispatch('setDrawerHidden', [false]);
  },

  render() {
    const firstItem = this.feed[0];

    if (!firstItem) {
      return <Rail categoryName={this.categoryName} />;
    }

    return (
      <Rail
        ref='rail'
        v-spatial-container
        onFocus={this.focus}
        on={this.$listeners}
        categoryName={this.categoryName}
      >
        <IntersectionObserverMarker
          class='rail-start-marker'
          start={{ options: { root: () => this.$refs.rail as HTMLElement }, callback: this.railStartObserved }}
          end={{ options: { root: () => this.$refs.rail as HTMLElement }, callback: this.railStartObserved }}
        />
        <SummaryFetcher
          key={firstItem.feedId}
          type={this.summaryType}
          summaryId={firstItem.feedId}
          onLoad={this.firstCardLoadedCallback}
          onRender={this.firstCardRendered}
          autoUpdate={this.autoUpdate}
          isCached={this.cacheSummary}
          itemSubType={firstItem.itemSubType}
          cardFocusedCallback={this.cardFocusedCallback}
          ref={'card-0'}
        />
        {this.feed.slice(1).map((feedItem, i) => (
          <SummaryFetcher
            key={feedItem.feedId}
            type={this.summaryType}
            summaryId={feedItem.feedId}
            autoUpdate={this.autoUpdate}
            isCached={this.cacheSummary}
            itemSubType={feedItem.itemSubType}
            cardFocusedCallback={this.cardFocusedCallback}
          />
        ))}
        {this.endReachedCallback != null && (
          <IntersectionObserverMarker
            class='rail-end-marker'
            start={{
              options: { root: () => this.$refs.rail as HTMLElement, rootMargin: '100%' },
              callback: this.railEndObserved,
            }}
          >
            <InlineLoader />
          </IntersectionObserverMarker>
        )}
      </Rail>
    );
  },
});

// VUE JSX HOT LOADER //
if (module.hot) require("/github/workspace/node_modules/vue-jsx-hot-loader/src/api.js")({ Vue: require('vue'), ctx: eval('this'), module: module, hotId: "_vue_jsx_hot-19703100/CardRail.vue" });